// src/components/kuestion/pages/ExamTemplates.jsx
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../../config/apiConfig';
import { useKuestionApi } from '../useKuestionApi';
import { PassMarkControl, passLabel } from '../campus/TemplateEditor';
import './ExamTemplates.css';

const CheckIcon = () => (<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3.5 8.5 6.5 11.5 12.5 4.5"/></svg>);
const SaveIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M5 3h8l3 3v9a1 1 0 01-1 1H5a1 1 0 01-1-1V4a1 1 0 011-1z"/><path d="M7 3v4h5V3"/><path d="M7 14h6v-3H7z"/></svg>);
const FolderIcon = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 5a1 1 0 011-1h4l2 2h6a1 1 0 011 1v8a1 1 0 01-1 1H4a1 1 0 01-1-1V5z"/></svg>);
const SearchIcon = () => (<svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><circle cx="7" cy="7" r="4.5"/><line x1="10.2" y1="10.2" x2="14" y2="14"/></svg>);
const CloseSmIcon = () => (<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="2" y1="2" x2="10" y2="10"/><line x1="10" y1="2" x2="2" y2="10"/></svg>);

const ColumnHeader = ({ label, filterKey, filters, updateFilter, activeSearch, setActiveSearch }) => {
  const isOpen = activeSearch === filterKey;
  const hasValue = filters[filterKey] && filters[filterKey].length > 0;
  return (
    <th>
      <div className="et-col-header">
        {isOpen ? (
          <div className="et-col-search-box">
            <input autoFocus type="text" placeholder={`Search ${label}...`} value={filters[filterKey]} onChange={e => updateFilter(filterKey, e.target.value)} />
            <button className="et-col-search-close" onClick={() => { setActiveSearch(null); updateFilter(filterKey, ''); }}><CloseSmIcon /></button>
          </div>
        ) : (
          <>
            <button className={`et-search-toggle ${hasValue ? 'et-search-active' : ''}`} onClick={() => setActiveSearch(filterKey)} title={`Search ${label}`}><SearchIcon /></button>
            <span className="et-col-label">{label}</span>
          </>
        )}
      </div>
    </th>
  );
};

const ExamTemplates = () => {
  const [dataSources, setDataSources] = useState([]);
  const [filters, setFilters] = useState({ examCode: '', module: '', type: '', dataSource: '', dateModified: '' });
  const [activeSearch, setActiveSearch] = useState(null);
  // pass_percentage (contract v4 §6): optional, 0–100. `passOn:false` sends null.
  const [settings, setSettings] = useState({ examName: '', totalScore: '', timeValue: '', timeUnit: 'minutes', shuffle: false, passOn: false, passValue: '70' });
  const [showLoadModal, setShowLoadModal] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving' | 'saved' | 'error'
  const [saveError, setSaveError] = useState('');
  const [searchParams] = useSearchParams();

  // Identity comes from the bearer token; no user_id is sent anywhere.
  const api = useKuestionApi();

  // ── Load question banks from backend ──────────────────────────────
  const loadBanks = useCallback(async () => {
    try {
      const data = await api.get(API_ENDPOINTS.KQ_BANKS);
      setDataSources((data || []).map(b => ({
        id: b.id, examCode: b.exam_code, module: b.module,
        type: b.question_type, source: b.data_source,
        count: b.question_count ?? b.total_questions ?? null,
        date: b.updated_at?.split('T')[0] || '', selected: false,
      })));
    } catch (e) { console.error('Failed to load banks:', e); }
  }, [api]);

  useEffect(() => { loadBanks(); }, [loadBanks]);

  // ── Save template ─────────────────────────────────────────────────
  const saveTemplate = async () => {
    const selected = dataSources.filter(d => d.selected).map(d => d.id);

    if (!settings.examName.trim()) {
      setSaveError('Give the template a name before saving.');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }
    // The server rejects an empty selection with `no_banks_selected`; catch it
    // here so the user isn't sent to the network to be told.
    if (!selected.length) {
      setSaveError('Select at least one question bank before saving.');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    const passN = parseInt(settings.passValue, 10);
    if (settings.passOn && (Number.isNaN(passN) || passN < 0 || passN > 100)) {
      setSaveError('The pass mark must be a whole number between 0 and 100.');
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 3000);
      return;
    }

    setSaveStatus('saving');
    setSaveError('');
    try {
      const timeLimit = settings.timeUnit === 'hours'
        ? parseInt(settings.timeValue || 0) * 60
        : parseInt(settings.timeValue || 90);

      await api.postJson(API_ENDPOINTS.KQ_TEMPLATES, {
        template_name: settings.examName,
        exam_name: settings.examName,
        total_score: parseInt(settings.totalScore) || 100,
        time_limit: timeLimit,          // always minutes, per contract Q4
        time_unit: settings.timeUnit,   // display label only
        shuffle: settings.shuffle,
        selected_banks: selected,
        pass_percentage: settings.passOn ? passN : null, // v4 §6 — nullable by design
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(null), 3000);
    } catch (e) {
      console.error('Save failed:', e);
      setSaveError(e.message);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  // ── Load templates list ───────────────────────────────────────────
  const loadTemplatesList = async () => {
    try {
      setSavedTemplates(await api.get(API_ENDPOINTS.KQ_TEMPLATES) || []);
    } catch (e) { console.error('Load templates failed:', e); }
    setShowLoadModal(true);
  };

  const applyTemplate = useCallback((tpl) => {
    setSettings({
      examName: tpl.exam_name || tpl.template_name || '',
      totalScore: String(tpl.total_score || 100),
      timeValue: String(tpl.time_unit === 'hours' ? Math.round(tpl.time_limit / 60) : tpl.time_limit || 90),
      timeUnit: tpl.time_unit || 'minutes',
      shuffle: tpl.shuffle || false,
      passOn: tpl.pass_percentage != null,
      passValue: tpl.pass_percentage != null ? String(tpl.pass_percentage) : '70',
    });
    // Select the banks that were in this template
    const bankIds = new Set(tpl.selected_banks || []);
    setDataSources(p => p.map(d => ({ ...d, selected: bankIds.has(d.id) })));
    setShowLoadModal(false);
  }, []);

  // "Edit questions" from the template editor arrives with ?id=; preload it.
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;
    let live = true;
    api.get(API_ENDPOINTS.KQ_TEMPLATE(id))
      .then((tpl) => { if (live && tpl) applyTemplate(tpl); })
      .catch(async () => {
        try {
          const all = await api.get(API_ENDPOINTS.KQ_TEMPLATES);
          const tpl = (all || []).find((t) => String(t.id) === String(id));
          if (live && tpl) applyTemplate(tpl);
        } catch { /* leave the form empty */ }
      });
    return () => { live = false; };
  }, [api, searchParams, applyTemplate]);

  // Wait on the response before dropping the row — an optimistic removal hides
  // a failed delete until the next reload.
  const deleteTemplate = async (tid) => {
    try {
      await api.del(API_ENDPOINTS.KQ_TEMPLATE(tid));
      setSavedTemplates(p => p.filter(t => t.id !== tid));
    } catch (e) {
      console.error('Delete template failed:', e);
      setSaveError(e.message);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(null), 4000);
    }
  };

  const toggleSelect = (id) => setDataSources(p => p.map(d => d.id === id ? { ...d, selected: !d.selected } : d));
  const toggleAll = () => {
    const filtered = filteredData;
    const allSel = filtered.length > 0 && filtered.every(d => d.selected);
    const filteredIds = new Set(filtered.map(d => d.id));
    setDataSources(p => p.map(d => filteredIds.has(d.id) ? { ...d, selected: !allSel } : d));
  };
  const updateFilter = (key, value) => setFilters(p => ({ ...p, [key]: value }));

  const filteredData = useMemo(() => {
    return dataSources.filter(d => {
      if (filters.examCode && !d.examCode.toLowerCase().includes(filters.examCode.toLowerCase())) return false;
      if (filters.module && !d.module.toLowerCase().includes(filters.module.toLowerCase())) return false;
      if (filters.type && !d.type.toLowerCase().includes(filters.type.toLowerCase())) return false;
      if (filters.dataSource && !d.source.toLowerCase().includes(filters.dataSource.toLowerCase())) return false;
      if (filters.dateModified && !d.date.includes(filters.dateModified)) return false;
      return true;
    });
  }, [dataSources, filters]);

  const selectedCount = filteredData.filter(d => d.selected).length;
  const allFilteredSelected = filteredData.length > 0 && filteredData.every(d => d.selected);
  const colProps = { filters, updateFilter, activeSearch, setActiveSearch };

  const selectedBanks = dataSources.filter(d => d.selected);
  const selectedQuestions = selectedBanks.reduce((n, d) => n + (d.count || 0), 0);

  // Re-uploading a corrected file creates a second, independent bank rather than
  // replacing the first (contract Q11). Two banks with the same exam code and
  // module look near-identical in this table, and selecting both silently
  // duplicates every question in the exam — so call it out.
  const duplicateSelections = useMemo(() => {
    const seen = new Map();
    dataSources.filter(d => d.selected).forEach(d => {
      const key = `${d.examCode}||${d.module}`;
      seen.set(key, (seen.get(key) || 0) + 1);
    });
    return [...seen.entries()].filter(([, n]) => n > 1).map(([key]) => key.split('||')[0]);
  }, [dataSources]);

  return (
    <div className="et-page" style={{ animation: 'kqFadeUp 0.5s ease both' }}>
      <div className="et-page-header">
        <div className="et-page-icon">
          <svg viewBox="0 0 42 42" fill="none"><rect x="8" y="4" width="26" height="34" rx="3" stroke="#DA4167" strokeWidth="1.5" fill="rgba(218,65,103,0.07)"/><rect x="16" y="2" width="10" height="5" rx="2" fill="#ffffff" stroke="#DA4167" strokeWidth="1.3"/><rect x="13" y="14" width="5" height="5" rx="1" stroke="#DA4167" strokeWidth="1.2"/><path d="M14.2 16.8l1.2 1.2 2.2-2.4" stroke="#DA4167" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/><line x1="21" y1="16.5" x2="30" y2="16.5" stroke="rgba(218,65,103,0.45)" strokeWidth="1.2" strokeLinecap="round"/><rect x="13" y="22" width="5" height="5" rx="1" stroke="#DA4167" strokeWidth="1.2"/><line x1="21" y1="24.5" x2="28" y2="24.5" stroke="rgba(218,65,103,0.45)" strokeWidth="1.2" strokeLinecap="round"/></svg>
        </div>
        <div><h1 className="et-page-title">Exam Templates</h1><p className="et-page-sub">Select data sources, configure exam settings, and manage templates.</p></div>
      </div>

      {saveStatus && (
        <div className={`et-save-toast ${saveStatus === 'saved' ? 'et-save-ok' : saveStatus === 'error' ? 'et-save-err' : ''}`}>
          {saveStatus === 'saving' && 'Saving template…'}
          {saveStatus === 'saved' && '✓ Template saved successfully!'}
          {saveStatus === 'error' && `✗ ${saveError || 'Failed to save. Please try again.'}`}
        </div>
      )}

      {duplicateSelections.length > 0 && (
        <div className="et-save-toast et-save-warn">
          ⚠ You have selected more than one bank for {duplicateSelections.join(', ')}.
          Those questions will appear twice in the exam.
        </div>
      )}

      <div className="et-section et-data-section">
        <div className="et-section-header">
          <span className="et-section-title">Data Selection</span>
          {selectedCount > 0 && (
            <span className="et-selected-badge">
              {selectedCount} selected{selectedQuestions > 0 ? ` · ${selectedQuestions} questions` : ''}
            </span>
          )}
        </div>
        <div className="et-table-wrap">
          <table className="et-table">
            <thead><tr>
              <th className="et-th-sel"><button className={`et-chk ${allFilteredSelected ? 'et-chk-on' : ''}`} onClick={toggleAll}>{allFilteredSelected && <CheckIcon />}</button></th>
              <ColumnHeader label="Exam Code" filterKey="examCode" {...colProps} />
              <ColumnHeader label="Module" filterKey="module" {...colProps} />
              <ColumnHeader label="Type" filterKey="type" {...colProps} />
              <th><div className="et-col-header"><span className="et-col-label">Questions</span></div></th>
              <ColumnHeader label="Data Source" filterKey="dataSource" {...colProps} />
              <ColumnHeader label="Date Modified" filterKey="dateModified" {...colProps} />
            </tr></thead>
            <tbody>
              {filteredData.length === 0 ? (
                <tr><td colSpan="7" className="et-empty-row">
                  <div className="et-empty-icon"><svg viewBox="0 0 42 42" fill="none"><rect x="8" y="4" width="26" height="34" rx="3" stroke="rgba(218,65,103,0.35)" strokeWidth="1.5"/><line x1="14" y1="16" x2="28" y2="16" stroke="rgba(218,65,103,0.25)" strokeWidth="1.2" strokeLinecap="round"/><line x1="14" y1="22" x2="24" y2="22" stroke="rgba(218,65,103,0.25)" strokeWidth="1.2" strokeLinecap="round"/></svg></div>
                  {dataSources.length === 0 ? 'No data sources loaded. Upload JSON files in Manage Data to populate this table.' : 'No results match your search filters.'}
                </td></tr>
              ) : filteredData.map(d => (
                <tr key={d.id} className={d.selected ? 'et-row-sel' : ''}>
                  <td className="et-td-sel"><button className={`et-chk ${d.selected ? 'et-chk-on' : ''}`} onClick={() => toggleSelect(d.id)}>{d.selected && <CheckIcon />}</button></td>
                  <td><span className="et-code-badge">{d.examCode}</span></td>
                  <td className="et-cell-name">{d.module}</td>
                  <td><span className={`et-type-badge ${d.type === 'multichoice' ? 'et-type-mcq' : d.type === 'draganddrop' ? 'et-type-dnd' : 'et-type-mixed'}`}>{d.type === 'multichoice' ? 'MCQ' : d.type === 'draganddrop' ? 'D&D' : 'Mixed'}</span></td>
                  <td className="et-cell-count">{d.count != null ? d.count : '—'}</td>
                  <td className="et-cell-source">{d.source}</td>
                  <td className="et-cell-date">{d.date}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="et-bottom-grid">
        <div className="et-section et-settings-section">
          <div className="et-section-header"><span className="et-section-title">Settings</span></div>
          <div className="et-settings-form">
            <div className="et-form-row et-form-inline"><label className="et-label-box">Exam Name</label><input className="et-input" type="text" placeholder="Enter exam name" value={settings.examName} onChange={e => setSettings(s => ({ ...s, examName: e.target.value }))} /></div>
            <div className="et-form-row et-form-inline"><label className="et-label-box">Total Score</label><input className="et-input" type="number" placeholder="e.g. 100" value={settings.totalScore} onChange={e => setSettings(s => ({ ...s, totalScore: e.target.value }))} /></div>
            <div className="et-form-row et-form-inline et-form-time"><label className="et-label-box">Time to Complete</label><input className="et-input et-time-input" type="number" placeholder="90" value={settings.timeValue} onChange={e => setSettings(s => ({ ...s, timeValue: e.target.value }))} /><select className="et-select" value={settings.timeUnit} onChange={e => setSettings(s => ({ ...s, timeUnit: e.target.value }))}><option value="minutes">Min</option><option value="hours">Hrs</option></select></div>
            <div className="et-form-row et-form-inline"><label className="et-label-box">Shuffle Questions</label><button className={`et-toggle ${settings.shuffle ? 'et-toggle-on' : ''}`} onClick={() => setSettings(s => ({ ...s, shuffle: !s.shuffle }))}><span className="et-toggle-knob" /></button></div>
            <div className="et-form-row" style={{ display: 'block' }}>
              <PassMarkControl
                on={settings.passOn}
                value={settings.passValue}
                questionCount={selectedQuestions}
                onToggle={(on) => setSettings(s => ({ ...s, passOn: on }))}
                onChange={(v) => setSettings(s => ({ ...s, passValue: v }))}
              />
            </div>
          </div>
        </div>
        <div className="et-section et-mgmt-section">
          <div className="et-section-header"><span className="et-section-title">Template Management</span></div>
          <div className="et-mgmt-actions">
            <button className="et-mgmt-btn et-mgmt-save" onClick={saveTemplate}><div className="et-mgmt-icon"><SaveIcon /></div><div className="et-mgmt-text"><span className="et-mgmt-label">Save Exam Template</span><span className="et-mgmt-hint">Save current configuration as a reusable template</span></div></button>
            <button className="et-mgmt-btn et-mgmt-load" onClick={loadTemplatesList}><div className="et-mgmt-icon et-mgmt-icon-load"><FolderIcon /></div><div className="et-mgmt-text"><span className="et-mgmt-label">Load Exam Template</span><span className="et-mgmt-hint">Browse and load a previously saved template</span></div></button>
          </div>
        </div>
      </div>

      {/* Portalled to <body>: .et-page has an animation that leaves a
          transform behind, and a transformed ancestor becomes the containing
          block for position:fixed children. */}
      {showLoadModal && ReactDOM.createPortal(
        <div className="et-modal-overlay" onClick={() => setShowLoadModal(false)}>
          <div className="et-modal" onClick={e => e.stopPropagation()}>
            <div className="et-modal-header"><span>Saved Templates</span><button className="et-modal-close" onClick={() => setShowLoadModal(false)}>✕</button></div>
            <div className="et-modal-body">
              {savedTemplates.length === 0 ? (
                <div className="et-modal-empty">No saved templates yet.</div>
              ) : savedTemplates.map(t => (
                <div key={t.id} className="et-tpl-card">
                  <div className="et-tpl-info">
                    <span className="et-tpl-name">{t.template_name}</span>
                    <span className="et-tpl-meta">{t.total_questions} questions · Score: {t.total_score} · {t.time_limit} min · Pass: {passLabel(t.pass_percentage)}</span>
                  </div>
                  <div className="et-tpl-actions">
                    <button className="et-tpl-load-btn" onClick={() => applyTemplate(t)}>Load</button>
                    <button className="et-tpl-del-btn" onClick={() => deleteTemplate(t.id)}>✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ExamTemplates;
